/**
 * Reglas de la actualización automática, en un solo lugar.
 *
 * Las pantallas del message log se refrescan solas mientras el operador mira
 * (y puede apagarlo con el switch del encabezado). Acá viven las cadencias, qué
 * mensaje ya no vale la pena volver a pedir y cómo se cuenta cuándo se actualizó
 * lo que está en pantalla. El mecanismo (polling secuencial, pausa con la
 * pestaña oculta) está en `hooks/use-polled-resource.ts`.
 */
import type { MessageStatus } from '../api/contracts';
import { formatDateTime, formatTime } from './format';

/**
 * Pausa entre el fin de una consulta y el comienzo de la siguiente, por pantalla.
 *
 * El grafo es liviano y es donde se ve avanzar una corrida, así que va rápido.
 * El listado es la consulta más cara (filtra y pagina sobre todo el log) y nadie
 * lo lee de a segundos. El detalle y la traza quedan en el medio.
 */
export const REFRESH_INTERVAL_MS = {
  messages: 10_000,
  runs: 10_000,
  messageDetail: 5_000,
  trace: 5_000,
  flowGraph: 2_000,
} as const;

/**
 * Estados de los que un mensaje ya no sale solo.
 *
 * `failed` no está: un fallido todavía tiene reintentos por delante. De estos tres
 * solo lo saca una persona (reprocesar una dead letter, revisar una cuarentena).
 */
const SETTLED_STATUSES: ReadonlySet<MessageStatus> = new Set<MessageStatus>([
  'processed',
  'dead',
  'quarantined',
]);

export function isSettledStatus(status: MessageStatus): boolean {
  return SETTLED_STATUSES.has(status);
}

/** La más reciente de varias actualizaciones (epoch ms); `null` si no hubo ninguna. */
export function latestUpdate(...updates: readonly (number | null)[]): number | null {
  let latest: number | null = null;
  for (const update of updates) {
    if (update !== null && (latest === null || update > latest)) {
      latest = update;
    }
  }
  return latest;
}

/** "cada 10 s", "cada 2 min". */
export function describeInterval(intervalMs: number): string {
  const seconds = Math.round(intervalMs / 1000);
  if (seconds < 60) {
    return `cada ${seconds} s`;
  }
  return `cada ${Math.round(seconds / 60)} min`;
}

const MINUTE_S = 60;
const HOUR_S = 60 * MINUTE_S;
const DAY_S = 24 * HOUR_S;

/**
 * Cuándo llegó el dato que se está mirando, dicho como lo diría una persona.
 *
 * Relativo mientras es reciente ("hace 4 s"), porque lo que importa es si está
 * fresco. Pasada una hora, la hora de Chile: "hace 83 min" obliga a hacer la
 * cuenta. Pasado un día, con la fecha, porque la hora sola ya es ambigua.
 */
export function describeLastUpdate(updatedAt: number, now: number): string {
  // Un reloj un poco atrasado respecto del dato no puede decir "hace -1 s".
  const elapsedS = Math.max(0, Math.floor((now - updatedAt) / 1000));
  if (elapsedS < 1) {
    return 'Actualizado recién';
  }
  if (elapsedS < MINUTE_S) {
    return `Actualizado hace ${elapsedS} s`;
  }
  if (elapsedS < HOUR_S) {
    return `Actualizado hace ${Math.floor(elapsedS / MINUTE_S)} min`;
  }
  const iso = new Date(updatedAt).toISOString();
  if (elapsedS < DAY_S) {
    return `Actualizado a las ${formatTime(iso)}`;
  }
  return `Actualizado el ${formatDateTime(iso)}`;
}
